import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router';
import { ChangePasswordPage } from './features/auth/ChangePasswordPage';
import { LoginPage } from './features/auth/LoginPage';
import { ConnectionsPage } from './features/connections/ConnectionsPage';
import { OnboardingPage } from './features/connections/OnboardingPage';
import { WhatsAppOnboardingPage } from './features/connections/WhatsAppOnboardingPage';
import { ConversationRoute } from './features/conversation/ConversationPane';
import { TeamPage } from './features/team/TeamPage';
import { BoardPage } from './features/board/BoardPage';
import { EmptyPane } from './features/inbox/EmptyPane';
import { InboxPage } from './features/inbox/InboxPage';
import { DataDeletionPage } from './features/public/DataDeletionPage';
import { useSession } from './lib/session';
import { FullPageMessage } from './ui/FullPageMessage';
import { ThemeController } from './ui/useTheme';

/** Decide la ruta según la sesión: sin sesión → entrar; contraseña temporal → cambiarla. */
function SessionGate({ need }: { need: 'guest' | 'pending' | 'active' }) {
  const session = useSession();
  const location = useLocation();
  if (session.isPending) return <FullPageMessage title="Abriendo Hilo…" />;
  if (session.isError) {
    return (
      <FullPageMessage
        title="No hay conexión con Hilo"
        detail="Revisa tu red. La página lo volverá a intentar al recargarla."
        action={{ label: 'Recargar', onClick: () => window.location.reload() }}
      />
    );
  }
  const data = session.data;
  if (!data) return need === 'guest' ? <Outlet /> : <Navigate to="/entrar" replace state={{ from: location.pathname }} />;
  if (data.mustChangePassword) return need === 'pending' ? <Outlet /> : <Navigate to="/cambiar-contrasena" replace />;
  return need === 'active' ? <Outlet /> : <Navigate to="/" replace />;
}

const router = createBrowserRouter([
  { element: <SessionGate need="guest" />, children: [{ path: '/entrar', element: <LoginPage /> }] },
  { element: <SessionGate need="pending" />, children: [{ path: '/cambiar-contrasena', element: <ChangePasswordPage /> }] },
  {
    element: <SessionGate need="active" />,
    children: [
      {
        path: '/',
        element: <InboxPage />,
        children: [
          { index: true, element: <EmptyPane /> },
          { path: 'conversaciones/:id', element: <ConversationRoute /> },
        ],
      },
      // Solo ADMIN (AdminShell redirige a la bandeja a los demás).
      { path: '/conexiones', element: <ConnectionsPage /> },
      { path: '/conexiones/whatsapp', element: <WhatsAppOnboardingPage /> },
      { path: '/conexiones/alta/:id', element: <OnboardingPage /> },
      { path: '/equipo', element: <TeamPage /> },
      // Todo el equipo: tareas internas.
      { path: '/tablero', element: <BoardPage /> },
    ],
  },
  // Pública, con o sin sesión: instrucciones y estado de las solicitudes de eliminación de Meta.
  { path: '/eliminacion-datos', element: <DataDeletionPage /> },
  { path: '*', element: <Navigate to="/" replace /> },
]);

export function App() {
  return (
    <>
      <ThemeController />
      <RouterProvider router={router} />
    </>
  );
}
